import type { ReactElement, ReactNode } from "react";
import {
  SOLVE_ACTION_DUE_BUCKET_LABELS,
  SOLVE_ACTION_SOURCE_LABELS,
  solveActionDueBucket,
  toLocalIsoDate,
  type SolveActionRow,
} from "@playbookai/widget-schemas";
import type { StatusTone } from "../../atoms/StatusDot/index.js";
import { StatusPill } from "../../atoms/StatusPill/index.js";
import type { WidgetBodyProps } from "../types.js";
import { useWidgetActions } from "../widget-actions.js";
import type { SolveActionsView } from "./types.js";

/**
 * Tones for the two source vocabularies, kept in ONE map each so a status can
 * never be coloured by the other source's rules.
 *
 * Neither `Done` is healthy-green: a finished action is a terminal fact, not a
 * reassurance, and colouring it green makes a closed pile read like an
 * on-track one — the same call `kaizenTable` already made for its plan
 * statuses. `Stopped` is neutral rather than critical for the same reason:
 * somebody decided to stop, and that decision is not a failure signal.
 */
const CONTAINMENT_TONES: Record<string, StatusTone> = {
  Open: "info",
  "In progress": "watch",
  Done: "neutral",
  Stopped: "neutral",
};

const COUNTERMEASURE_TONES: Record<string, StatusTone> = {
  Proposed: "neutral",
  Approved: "info",
  "In Design": "info",
  Trial: "watch",
  Done: "neutral",
};

function statusTone(row: SolveActionRow): StatusTone {
  const tones =
    row.source === "containment" ? CONTAINMENT_TONES : COUNTERMEASURE_TONES;
  return tones[row.status] ?? "neutral";
}

/**
 * The timing cell, and the one place the two sources must not be blended.
 *
 * A containment action shows its structured ETA and, when overdue, says so.
 * A countermeasure shows its implementation window VERBATIM — it is prose the
 * author typed, so it is never reformatted, compared to today, or decorated
 * with an overdue marker. A row with neither shows an em dash, which is the
 * honest reading of "nobody wrote one".
 */
function TimingCell({ row }: { row: SolveActionRow }): ReactElement {
  if (row.source === "countermeasure") {
    const text = row.windowText?.trim() ?? "";
    return text === "" ? (
      <span className="text-pb-text-muted">—</span>
    ) : (
      <span className="text-pb-text-secondary" title={text}>
        {text}
      </span>
    );
  }
  if (row.due === null) return <span className="text-pb-text-muted">—</span>;
  return (
    <span
      className={row.overdue ? "text-pb-red-text" : "text-pb-text-secondary"}
    >
      {row.due}
      {row.overdue ? <span className="ms-1 font-medium">· Overdue</span> : null}
    </span>
  );
}

function groupKeyFor(
  row: SolveActionRow,
  groupBy: SolveActionsView["groupBy"],
  today: string,
): string {
  switch (groupBy) {
    case "solve":
      return row.solveId;
    case "source":
      return SOLVE_ACTION_SOURCE_LABELS[row.source];
    case "due":
      return SOLVE_ACTION_DUE_BUCKET_LABELS[solveActionDueBucket(row, today)];
    case "none":
      return "";
  }
}

/**
 * SOLVE Actions — a READ-ONLY cross-SOLVE list of Containment Plan actions and
 * Countermeasure Table rows.
 *
 * Data-blind: every row arrives resolved, the only interaction is navigation
 * through the host's `openSolveAction`, and there is no `onChange` path at all
 * — completing or re-statusing an action stays on the SOLVE canvas.
 */
export function SolveActionsWidget({
  data,
}: WidgetBodyProps<SolveActionsView>): ReactElement {
  const actions = useWidgetActions();
  const openAction = actions?.openSolveAction;
  const today = toLocalIsoDate(new Date());
  const showSolveOnRow = data.groupBy !== "solve";

  const notes: ReactNode[] = [];
  if (data.dueFilterExcludesCountermeasures) {
    notes.push(
      <li key="due-note">
        A due-date filter is active, so Countermeasure Table rows are not shown
        — their implementation window is free text and carries no date.
      </li>,
    );
  }
  if (data.unavailableSolveIds.length > 0) {
    notes.push(
      <li key="unavailable">
        {data.unavailableSolveIds.length === 1
          ? "1 selected SOLVE is no longer available to you and was skipped."
          : `${data.unavailableSolveIds.length} selected SOLVEs are no longer available to you and were skipped.`}
      </li>,
    );
  }
  if (data.scopeTruncated) {
    notes.push(
      <li key="scope">
        You can see more SOLVEs than this widget reads at once — only your most
        recently updated ones were included. Select specific SOLVEs to be sure
        of the scope.
      </li>,
    );
  }
  if (data.truncated) {
    notes.push(
      <li key="truncated">
        Showing the first {data.rows.length} of {data.matchedCount} matching
        actions, most urgent first. Narrow the filters to see the rest.
      </li>,
    );
  }

  if (data.rows.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {data.singleSolveTitle ? (
          <p className="text-dense-1 text-pb-text-muted">
            {data.singleSolveTitle}
          </p>
        ) : null}
        <div className="flex min-h-22 flex-col items-start justify-center gap-1 py-2">
          <p className="text-sm font-medium text-pb-text">
            No actions match these settings
          </p>
          <p className="text-xs text-pb-text-secondary">
            Containment Plan actions and Countermeasure Table rows from the
            SOLVEs you can access appear here.
          </p>
        </div>
        {notes.length > 0 ? (
          <ul className="space-y-1 text-dense-2 text-pb-text-muted">{notes}</ul>
        ) : null}
      </div>
    );
  }

  // Stable groups preserve source order within each group and identify SOLVEs by id.
  const groups = new Map<string, SolveActionRow[]>();
  for (const row of data.rows) {
    const key = groupKeyFor(row, data.groupBy, today);
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  const groupedRows = [...groups.entries()].flatMap(([key, rows]) =>
    rows.map((row, index) => ({
      row,
      heading:
        index === 0 && key !== ""
          ? data.groupBy === "solve"
            ? row.solveTitle
            : key
          : null,
    })),
  );

  return (
    <div className="flex flex-col gap-2">
      {data.singleSolveTitle ? (
        <p className="text-dense-1 text-pb-text-muted">
          {data.singleSolveTitle}
        </p>
      ) : null}

      <ul className="flex flex-col">
        {groupedRows.map(({ row, heading }) => {
          const body = (
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex min-w-0 items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm leading-snug text-pb-text">
                  {row.action.trim() === "" ? "Untitled action" : row.action}
                </span>
                <StatusPill tone={statusTone(row)}>{row.status}</StatusPill>
              </span>
              <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-dense-1 leading-snug text-pb-text-muted">
                {showSolveOnRow ? (
                  <span className="truncate">{row.solveTitle}</span>
                ) : null}
                {data.groupBy !== "source" ? (
                  <span className="truncate">
                    {SOLVE_ACTION_SOURCE_LABELS[row.source]}
                  </span>
                ) : null}
                <span className="truncate">
                  {row.owner.trim() === "" ? "No owner" : row.owner}
                </span>
                <TimingCell row={row} />
                {data.columns.includes("priority") && row.priority !== null ? (
                  <span className="truncate">Priority: {row.priority}</span>
                ) : null}
                {data.columns.includes("impactEffort") &&
                row.impact !== null &&
                row.effort !== null ? (
                  <span className="truncate">
                    Impact {row.impact} · Effort {row.effort}
                  </span>
                ) : null}
                {data.columns.includes("effectiveness") &&
                row.effectiveness !== null ? (
                  <span className="truncate">{row.effectiveness}</span>
                ) : null}
              </span>
            </span>
          );
          return (
            <li key={row.id}>
              {heading ? (
                <p className="mt-2 mb-1 text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted first:mt-0">
                  {heading}
                </p>
              ) : null}
              {openAction ? (
                // eslint-disable-next-line ds/no-hand-built -- kept: a clickable row with its own inset and a pulled-out hover; ListItem has no inline padding and its own row layout, which AP-07's selection fill does not cover.
                <button
                  type="button"
                  onClick={() =>
                    openAction({
                      solveId: row.solveId,
                      solveTitle: row.solveTitle,
                      widgetId: row.widgetId,
                    })
                  }
                  className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-2.5 rounded-md px-2 py-2 text-start transition-colors hover:pb-overlay-paint focus-ring"
                >
                  {body}
                </button>
              ) : (
                <div className="flex items-start gap-2.5 py-1.5">{body}</div>
              )}
            </li>
          );
        })}
      </ul>

      {notes.length > 0 ? (
        <ul className="space-y-1 text-dense-2 text-pb-text-muted">{notes}</ul>
      ) : null}
    </div>
  );
}
